/**
 * Zustand store для образовательных коллекций
 * apps/web/src/stores/collections.ts
 */

import { create } from 'zustand';
import {
  CollectionListItem,
  Collection,
  UserCollectionProgress,
  ModuleQuestion,
  ModuleStartResponse,
} from '@beshek/shared';
import * as api from '../lib/collections-api';

// ==================== Types ====================

interface Category {
  id: string;
  name: string;
  count: number;
}

interface GameSession {
  id: string;
  collectionId: string;
  moduleId: string;
  questions: ModuleQuestion[];
  currentIndex: number;
  answers: Array<{
    value?: string;
    questionId: string;
    answerIndex: number;
    points?: { x: number; y: number }[];
    point?: { x: number; y: number };
    placements?: Record<string, string>;
    assignment?: Record<string, string>;
    order?: string[];
    lineId?: string;
    timeSpent: number;
    correct: boolean;
  }>;
  startedAt: number;
  questionStartedAt: number;
}

interface GameResult {
  score: number;
  passed: boolean;
  xpEarned: number;
  newBestScore: boolean;
  moduleCompleted: boolean;
  collectionCompleted: boolean;
  correctCount: number;
  totalCount: number;
}

type ViewState = 'list' | 'detail' | 'playing' | 'result';


interface CollectionsState {
  // UI State
  viewState: ViewState;
  isLoading: boolean;
  error: string | null;

  // Data
  categories: Category[];
  collections: CollectionListItem[];
  currentCollection: Collection | null;
  currentProgress: UserCollectionProgress | null;
  hasAccess: boolean;

  // Filters
  selectedCategory: string | null;
  selectedDifficulty: string | null;

  // Game State
  session: GameSession | null;
  result: GameResult | null;

  // Actions
  loadCategories: () => Promise<void>;
  loadCollections: () => Promise<void>;
  loadCollectionDetail: (id: string) => Promise<void>;
  selectCategory: (category: string | null) => void;
  selectDifficulty: (difficulty: string | null) => void;

  // Game Actions
  startModule: (collectionId: string, moduleId: string) => Promise<ModuleStartResponse | null>;
  answerQuestion: (answerIndex: number, point?: { x: number; y: number }, known?: boolean, placements?: Record<string, string>, order?: string[], points?: { x: number; y: number }[], lineId?: string, assignment?: Record<string, string>, value?: string) => void;
  completeModule: () => Promise<void>;
  resetGame: () => void;

  // Navigation
  goToList: () => void;
  goToDetail: (id: string) => void;
}

// ==================== Store ====================

export const useCollectionsStore = create<CollectionsState>((set, get) => ({
  // Initial State
  viewState: 'list',
  isLoading: false,
  error: null,
  categories: [],
  collections: [],
  currentCollection: null,
  currentProgress: null,
  hasAccess: true,
  selectedCategory: null,
  selectedDifficulty: null,
  session: null,
  result: null,

  // ==================== Data Loading ====================

  loadCategories: async () => {
    try {
      const { categories } = await api.getCollectionCategories();
      set({ categories });
    } catch (error) {
      console.error('[Collections Store] Error loading categories:', error);
    }
  },

  loadCollections: async () => {
    const { selectedCategory, selectedDifficulty } = get();

    set({ isLoading: true, error: null });

    try {
      const { collections } = await api.getCollections({
        category: selectedCategory || undefined,
        difficulty: selectedDifficulty || undefined,
      });

      set({ collections, isLoading: false });
    } catch (error) {
      console.error('[Collections Store] Error loading collections:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка загрузки',
        isLoading: false,
      });
    }
  },

  loadCollectionDetail: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const { collection, userProgress, hasAccess } = await api.getCollectionDetail(id);

      set({
        currentCollection: collection,
        currentProgress: userProgress,
        hasAccess,
        viewState: 'detail',
        isLoading: false,
      });
    } catch (error) {
      console.error('[Collections Store] Error loading collection:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка загрузки',
        isLoading: false,
      });
    }
  },

  // ==================== Filters ====================

  selectCategory: (category: string | null) => {
    set({ selectedCategory: category });
    get().loadCollections();
  },

  selectDifficulty: (difficulty: string | null) => {
    set({ selectedDifficulty: difficulty });
    get().loadCollections();
  },

  // ==================== Game Actions ====================

  startModule: async (collectionId: string, moduleId: string) => {
    set({ isLoading: true, error: null });

    try {
      const result = await api.startModule(collectionId, moduleId);

      // Если есть урок - возвращаем результат, не устанавливаем session
      if (result.hasLesson === true) {
        set({ isLoading: false });
        return result;
      }

      // Нет урока - устанавливаем session для теста
      const { questions, session } = result as any;
      set({
        session: {
          id: session.id,
          collectionId,
          moduleId,
          questions,
          currentIndex: 0,
          answers: [],
          startedAt: Date.now(),
          questionStartedAt: Date.now(),
        },
        viewState: 'playing',
        isLoading: false,
      });

      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
      return result;
    } catch (error) {
      console.error('[Collections Store] Error starting module:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка запуска',
        isLoading: false,
      });
      return null;
    }
  },

  answerQuestion: (answerIndex: number, point?: { x: number; y: number }, known?: boolean, placements?: Record<string, string>, order?: string[], points?: { x: number; y: number }[], lineId?: string, assignment?: Record<string, string>, value?: string) => {
    const { session } = get();
    if (!session) return;

    const currentQuestion = session.questions[session.currentIndex];
    const timeSpent = Date.now() - session.questionStartedAt;
    // known — правильность уже известна от сервера (клик по фото); иначе локальная проверка
    const correct = known ?? (answerIndex === currentQuestion.correctIndex);

    // Haptic feedback
    if (correct) {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } else {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }

    const newAnswers = [
      ...session.answers,
      {
        questionId: currentQuestion.id,
        answerIndex,
        point,
        points,
        lineId,
        assignment,
        value,
        placements,
        order,
        timeSpent,
        correct,
      },
    ];

    const nextIndex = session.currentIndex + 1;
    const isLastQuestion = nextIndex >= session.questions.length;

    set({
      session: {
        ...session,
        answers: newAnswers,
        currentIndex: nextIndex,
        questionStartedAt: Date.now(),
      },
    });

    // Автоматически завершаем если последний вопрос
    if (isLastQuestion) {
      // Небольшая задержка чтобы показать результат последнего ответа
      setTimeout(() => {
        get().completeModule();
      }, 1500);
    }
  },

  completeModule: async () => {
    const { session } = get();
    if (!session) return;

    set({ isLoading: true });

    try {
      const result = await api.completeModule(
        session.collectionId,
        session.moduleId,
        session.id,
        session.answers.map((a) => ({
          questionId: a.questionId,
          answerIndex: a.answerIndex,
          point: a.point,
          points: a.points,
          assignment: a.assignment,
          value: a.value,
          placements: a.placements,
          order: a.order,
          lineId: a.lineId,
          timeSpent: a.timeSpent,
        }))
      );

      const correctCount = session.answers.filter((a) => a.correct).length;

      set({
        result: {
          score: result.score,
          passed: result.passed,
          xpEarned: result.xpEarned,
          newBestScore: result.newBestScore,
          moduleCompleted: result.moduleCompleted,
          collectionCompleted: result.collectionCompleted,
          // сервер знает верные ответы, локальный счёт их не видит (показывал «0 из 8»)
          correctCount: (result as any).correctCount ?? correctCount,
          totalCount: (result as any).totalCount ?? session.answers.length,
        },
        currentProgress: result.progress,
        viewState: 'result',
        isLoading: false,
      });

      // Haptic feedback
      if (result.passed) {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      }
    } catch (error) {
      console.error('[Collections Store] Error completing module:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка сохранения',
        isLoading: false,
      });
    }
  },

  resetGame: () => {
    set({
      session: null,
      result: null,
    });
  },

  // ==================== Navigation ====================

  goToList: () => {
    set({
      viewState: 'list',
      currentCollection: null,
      currentProgress: null,
      session: null,
      result: null,
    });
    get().loadCollections();
  },

  goToDetail: (id: string) => {
    get().loadCollectionDetail(id);
  },
}));

// ==================== Selectors ====================

export const selectCurrentQuestion = (state: CollectionsState) => {
  if (!state.session) return null;
  return state.session.questions[state.session.currentIndex] || null;
};

export const selectProgress = (state: CollectionsState) => {
  if (!state.session) return { current: 0, total: 0 };
  return {
    current: state.session.currentIndex + 1,
    total: state.session.questions.length,
  };
};

export const selectModuleStatus = (
  state: CollectionsState,
  moduleId: string
): 'locked' | 'available' | 'started' | 'completed' => {
  const progress = state.currentProgress?.moduleProgress[moduleId];

  if (progress?.completed) return 'completed';
  if (progress?.started) return 'started';

  // Проверяем доступность (это упрощённая логика, полная на сервере)
  return 'available';
};